(() => {
  if (window.PawtyAnalytics) return;

  const MEASUREMENT_ID = 'G-S6Z03CC4WK';
  const SCHEMA_VERSION = 'pawty_product_funnel_v1';
  const DATA_LAYER_WAIT = 650;
  const DEDUPE_WINDOW = 5000;
  const pageInstance = crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const emitted = new Map();
  const listImpressions = new Set();

  window.dataLayer = window.dataLayer || [];
  const originalPush = window.dataLayer.push.bind(window.dataLayer);
  window.dataLayer.push = (...entries) => {
    return originalPush(...entries);
  };

  const money = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? Math.round(number * 100) / 100 : null;
  };
  const positiveInteger = (value) => {
    const number = Number(value);
    return Number.isInteger(number) && number > 0 ? number : null;
  };
  const digits = (value) => {
    const text = String(value || '');
    return /^\d+$/.test(text) ? text : null;
  };
  const cleanText = (value, maxLength) => {
    const text = String(value || '').trim();
    return text ? text.slice(0, maxLength) : null;
  };
  const variantId = (line) => digits(line?.variant_id || line?.variantId || line?.id);
  const productId = (line) => digits(line?.product_id || line?.productId);
  const normalizeLine = (line) => {
    const id = variantId(line);
    if (!id) return null;
    const quantity = positiveInteger(line?.quantity);
    const cents = line?.final_price ?? line?.price ?? null;
    return {
      item_id: id,
      product_id: productId(line),
      item_name: cleanText(line?.product_title || line?.title || line?.name, 200) || 'Product',
      price: cents == null ? null : money(Number(cents) / 100),
      quantity,
      item_list_id: null,
      item_list_name: null,
      index: null,
    };
  };

  const environment = () => {
    const preview = new URLSearchParams(window.location.search).has('preview_theme_id');
    const themeRole = String(window.Shopify?.theme?.role || '').toLowerCase();
    return preview || themeRole === 'unpublished' || themeRole === 'development' || window.Shopify?.designMode
      ? 'preview'
      : 'production';
  };
  const campaignFields = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: cleanText(params.get('utm_source'), 80),
      utm_medium: cleanText(params.get('utm_medium'), 80),
      utm_campaign: cleanText(params.get('utm_campaign'), 160),
      utm_content: cleanText(params.get('utm_content'), 160),
      campaign_id: digits(params.get('utm_id')),
      adset_id: digits(params.get('utm_term')),
      ad_id: digits(params.get('meta_ad_id')),
    };
  };
  const signature = (name, item, value, suffix = '') =>
    [name, item?.item_id || 'none', item?.quantity ?? 'null', value ?? 'null', pageInstance, suffix].join(':');
  const recentlyEmitted = (key) => Date.now() - (emitted.get(key) || 0) < DEDUPE_WINDOW;

  const emitEnvelope = ({ name, items, value = null, source = 'client', list = {}, transactionId = null, suffix = '' }) => {
    if (!analyticsAllowed()) return null;
    const safeItems = (items || []).filter(Boolean);
    if (!safeItems.length) return null;
    const key = signature(name, safeItems[0], value, suffix);
    if (recentlyEmitted(key)) return null;
    emitted.set(key, Date.now());
    const currentEnvironment = environment();
    const envelope = {
      schema_version: SCHEMA_VERSION,
      event_name: name,
      event_id: crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      event_time: new Date().toISOString(),
      source_authority: source,
      environment: currentEnvironment,
      currency: 'USD',
      value,
      list_id: list.id ?? null,
      list_name: list.name ?? null,
      list_position: list.position ?? null,
      media_position: list.mediaPosition ?? null,
      ...campaignFields(),
      transaction_id: transactionId,
      is_test: currentEnvironment !== 'production',
      items: safeItems,
    };
    window.dataLayer.push({ event: 'pawty_product_funnel', pawty_event: envelope });
    document.dispatchEvent(new CustomEvent('pawty:product-funnel', { detail: envelope }));
    if (['view_item_list', 'select_item', 'checkout_click'].includes(name)) void sendToGa4(envelope);
    return envelope;
  };

  const analyticsAllowed = () => {
    const privacy = window.Shopify?.customerPrivacy;
    return !privacy || typeof privacy.analyticsProcessingAllowed !== 'function' || privacy.analyticsProcessingAllowed();
  };
  let cartRequest = null;
  const getCart = () => {
    if (!cartRequest) {
      cartRequest = fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } })
        .then((response) => {
          if (!response.ok) throw new Error(`cart.js ${response.status}`);
          return response.json();
        })
        .finally(() => { cartRequest = null; });
    }
    return cartRequest;
  };

  let googleTagPromise = null;
  const loadGoogleTag = () => {
    if (googleTagPromise) return googleTagPromise;
    googleTagPromise = new Promise((resolve, reject) => {
      window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
      window.gtag('js', new Date());
      window.gtag('config', MEASUREMENT_ID, { send_page_view: false });
      const existing = document.querySelector(`script[data-pawty-ga4-view-cart="${MEASUREMENT_ID}"]`);
      if (existing) {
        if (existing.dataset.loaded === 'true') resolve();
        else {
          existing.addEventListener('load', resolve, { once: true });
          existing.addEventListener('error', reject, { once: true });
        }
        return;
      }
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
      script.dataset.pawtyGa4ViewCart = MEASUREMENT_ID;
      script.addEventListener('load', () => { script.dataset.loaded = 'true'; resolve(); }, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });
    return googleTagPromise;
  };

  const sendToGa4 = async (envelope) => {
    if (!envelope || !analyticsAllowed()) return;
    try {
      await loadGoogleTag();
      if (!analyticsAllowed()) return;
      window.gtag('event', envelope.event_name, {
        send_to: MEASUREMENT_ID,
        event_id: envelope.event_id,
        currency: envelope.currency,
        value: envelope.value,
        item_list_id: envelope.list_id,
        item_list_name: envelope.list_name,
        items: envelope.items,
      });
    } catch (error) {
      console.warn('[pawty-analytics] Unable to send funnel event');
    }
  };

  const trackAddToCart = (line) => {
    const item = normalizeLine(line);
    if (!item) return;
    const value = item.price == null || item.quantity == null ? null : money(item.price * item.quantity);
    window.setTimeout(() => {
      const actionSignature = `${item.item_id}:${item.quantity}:${value}`;
      const envelope = emitEnvelope({ name: 'add_to_cart', items: [item], value, suffix: `confirmed-${actionSignature}` });
      if (!envelope || !analyticsAllowed()) return;
      window.dataLayer.push({
        event: 'add_to_cart', event_id: envelope.event_id, pawty_fallback: true,
        ecommerce: { currency: 'USD', value, items: [item] },
      });
    }, DATA_LAYER_WAIT);
  };

  let drawerOpenInstance = 0;
  let isCartDrawerOpen = false;
  const nextFrame = () => new Promise((resolve) => window.requestAnimationFrame(resolve));
  const trackViewCart = async () => {
    await nextFrame();
    await nextFrame();
    if (!isCartDrawerOpen || !analyticsAllowed()) return;
    try {
      const cart = await getCart();
      if (!isCartDrawerOpen || !analyticsAllowed()) return;
      const items = (cart.items || []).map(normalizeLine).filter(Boolean);
      if (!items.length) return;
      const value = money(Number(cart.total_price) / 100);
      const envelope = emitEnvelope({ name: 'cart_shown', items, value, suffix: `open-${drawerOpenInstance}` });
      if (!envelope) return;
      await loadGoogleTag();
      if (!isCartDrawerOpen || !analyticsAllowed()) return;
      window.gtag('event', 'view_cart', { send_to: MEASUREMENT_ID, event_id: envelope.event_id, currency: 'USD', value, items });
    } catch (error) {
      console.warn('[pawty-analytics] Unable to record cart view');
    }
  };

  const productCatalog = () => {
    const products = window.meta?.products || window.ShopifyAnalytics?.meta?.products || [];
    return new Map(products.map((product) => [String(product.id), product]));
  };
  const selectedVariantId = (scope = document) => {
    const explicit = scope?.dataset?.variantId || scope?.querySelector?.('[data-variant-id]')?.dataset?.variantId;
    const linked = scope?.querySelector?.('a[href*="variant="]')?.href;
    const linkedId = linked ? new URL(linked, window.location.origin).searchParams.get('variant') : null;
    const formId = scope === document ? scope.querySelector('form[action*="/cart/add"] [name="id"]')?.value : null;
    const queryId = scope === document ? new URLSearchParams(window.location.search).get('variant') : null;
    return digits(explicit || linkedId || formId || queryId);
  };
  const productItem = (product, index = null, preferredVariantId = null) => {
    const variant = product?.variants?.find((candidate) => digits(candidate.id) === digits(preferredVariantId)) || product?.variants?.[0];
    const id = digits(variant?.id);
    if (!id) return null;
    const variantName = cleanText(variant?.name, 200);
    const fallbackName = variantName?.split(' - ')[0] || null;
    return {
      item_id: id,
      product_id: digits(product.id),
      item_name: cleanText(product.title, 200) || fallbackName || 'Product',
      price: variant.price == null ? null : money(Number(variant.price) / 100),
      quantity: 1,
      item_list_id: null,
      item_list_name: null,
      index,
    };
  };
  const cardItem = (card, index = null) => {
    if (!card) return null;
    const fallback = {
      id: card.dataset.productId,
      title: card.dataset.productTitle,
      variants: [{ id: card.dataset.variantId, price: card.dataset.productPrice }],
    };
    return productItem(productCatalog().get(card.dataset.productId) || fallback, index, selectedVariantId(card));
  };
  const listContext = () => {
    const resourceId = digits(window.ShopifyAnalytics?.meta?.page?.resourceId);
    const name = cleanText(document.querySelector('h1')?.textContent, 120);
    return { id: resourceId ? `collection:${resourceId}` : null, name };
  };

  let listObserver = null;
  const observeProductCards = () => {
    listObserver?.disconnect();
    const catalog = productCatalog();
    const cards = [...document.querySelectorAll('.product-grid__item[data-product-id], .family-browser [data-product-id]')];
    if (!cards.length || !('IntersectionObserver' in window)) return;
    const list = listContext();
    listObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) return;
        const card = entry.target;
        const position = cards.indexOf(card) + 1;
        const item = cardItem(card, position);
        if (!item) return;
        const key = `${list.id || 'list'}:${item.item_id}`;
        window.setTimeout(() => {
          if (!card.isConnected || listImpressions.has(key)) return;
          const rect = card.getBoundingClientRect();
          if (!(rect.bottom > 0 && rect.top < window.innerHeight)) return;
          listImpressions.add(key);
          item.item_list_id = list.id;
          item.item_list_name = list.name;
          emitEnvelope({ name: 'view_item_list', items: [item], value: null, list: { ...list, position }, suffix: key });
        }, 500);
      });
    }, { threshold: [0.5] });
    cards.forEach((card) => listObserver.observe(card));
  };

  const trackPdpView = () => {
    const pageType = window.ShopifyAnalytics?.meta?.page?.pageType;
    if (pageType !== 'product') return;
    const item = productItem([...productCatalog().values()][0], null, selectedVariantId());
    if (item) emitEnvelope({ name: 'view_item', items: [item], value: item.price, suffix: 'pdp-load' });
  };
  let checkoutSyncTimer = 0;
  let lastCartSnapshot = null;
  const syncCheckoutLabel = () => {
    window.clearTimeout(checkoutSyncTimer);
    checkoutSyncTimer = window.setTimeout(async () => {
      const buttons = [...document.querySelectorAll('.cart__checkout-button')];
      if (!buttons.length) return;
      const label = 'Start your portrait';
      try {
        const cart = await getCart();
        lastCartSnapshot = cart;
      } catch (error) {}
      buttons.forEach((button) => {
        const text = button.querySelector('.button-text') || button;
        if (text.textContent.trim() !== label) text.textContent = label;
      });
    }, 80);
  };

  document.addEventListener('click', (event) => {
    const cardLink = event.target.closest('.product-grid__item[data-product-id] a.product-card__link, .family-browser a[data-product-id]');
    if (cardLink) {
      const card = cardLink.matches('[data-product-id]') ? cardLink : cardLink.closest('.product-grid__item[data-product-id]');
      const cards = [...document.querySelectorAll('.product-grid__item[data-product-id], .family-browser [data-product-id]')];
      const position = cards.indexOf(card) + 1;
      const list = listContext();
      const item = cardItem(card, position);
      if (item) {
        item.item_list_id = list.id;
        item.item_list_name = list.name;
        emitEnvelope({ name: 'select_item', items: [item], value: item.price, list: { ...list, position }, suffix: `click-${position}` });
      }
    }
    const checkout = event.target.closest('.cart__checkout-button');
    if (checkout && !checkout.disabled) {
      const cart = lastCartSnapshot;
      const items = (cart?.items || []).map(normalizeLine).filter(Boolean);
      if (items.length) {
        emitEnvelope({ name: 'checkout_click', items, value: money(Number(cart.total_price) / 100), suffix: 'navigation' });
      }
    }
  }, true);

  document.addEventListener('theme-drawer:open', (event) => {
    if (event.target?.id !== 'cart-drawer' || isCartDrawerOpen) return;
    isCartDrawerOpen = true;
    drawerOpenInstance += 1;
    syncCheckoutLabel();
    trackViewCart();
  });
  document.addEventListener('theme-drawer:close', (event) => {
    if (event.target?.id === 'cart-drawer') isCartDrawerOpen = false;
  });

  const observer = new MutationObserver((records) => {
    const cartChanged = records.some((record) => [...record.addedNodes].some((node) =>
      node.nodeType === 1 && (node.matches?.('.cart__checkout-button, .cart-totals, cart-items-component') || node.querySelector?.('.cart__checkout-button'))
    ));
    if (cartChanged) syncCheckoutLabel();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  window.PawtyAnalytics = { trackAddToCart, trackViewCart, syncCheckoutLabel };
  document.addEventListener('DOMContentLoaded', () => {
    observeProductCards();
    trackPdpView();
    syncCheckoutLabel();
  }, { once: true });
})();
